import { useState } from 'react';
import PropTypes from 'prop-types';

import { formatDate, getClassNames, getFirstDayOfCurrentMonth, getLastDayOfCurrentMonth } from '../../../../utils/util';

import DateRangeDropdown from '../../../common/inputs/dropdown/date-range-dropdown/DateRangeDropdown';
import Button from '../../../common/button/Button';
import UserMetricsPieChart from './user-metrics-pie-chart/UserMetricsPieChart';
import Table from '../../../common/table/Table';

import './user-metrics.scss';

const UserMetrics = ({ userMetrics, retrieveUserMetrics, downloadExcel }) => {
  const [fromDate, setFromDate] = useState(getFirstDayOfCurrentMonth());
  const [toDate, setToDate] = useState(getLastDayOfCurrentMonth());
  const [showTable, setShowTable] = useState(false);

  const handleFromDateChange = (date) => {
    setFromDate(date);
    retrieveUserMetrics(date, toDate);
  };

  const handleToDageChange = (date) => {
    setToDate(date);
    retrieveUserMetrics(fromDate, date);
  };

  const renderGraphs = () => {
    return (
      <>
        <section className="user-metrics__current-statistics">
          <h2 className="user-metrics__current-statistics__header">Current Statistics</h2>

          <section className="user-metrics__current-statistics__content">
            <div className="user-metrics__current-statistics__statistic">
              <div className="user-metrics__current-statistics__statistic__bubble">{userMetrics.activeUsers}</div>
              <p className="user-metrics__current-statistics__statistic__heading">Active Users</p>
              <p className="user-metrics__current-statistics__statistic__subheading">
                Logged in within the last 7 days
              </p>
            </div>

            <div className="user-metrics__current-statistics__statistic">
              <div className="user-metrics__current-statistics__statistic__bubble">{userMetrics.totalUsers}</div>
              <p className="user-metrics__current-statistics__statistic__heading">Total Users</p>
              <p className="user-metrics__current-statistics__statistic__subheading">Currently registered on the app</p>
            </div>

            <div className="user-metrics__current-statistics__statistic">
              <div className="user-metrics__current-statistics__statistic__bubble">{userMetrics.totalCompanies}</div>
              <p className="user-metrics__current-statistics__statistic__heading">Total Companies</p>
              <p className="user-metrics__current-statistics__statistic__subheading">Currently verified on the app</p>
            </div>
          </section>
        </section>

        <section className="user-metrics__cumulative-statistics">
          <section className="user-metrics__cumulative-statistics__header">
            <h2>Cumulative Statistics</h2>
            <DateRangeDropdown
              fromDate={fromDate}
              toDate={toDate}
              onFromDateChange={handleFromDateChange}
              onToDateChange={handleToDageChange}
            />
          </section>

          <section className="user-metrics__cumulative-statistics__content">
            <UserMetricsPieChart
              title="Registrations"
              outerRingData={[
                { value: 100 - Number(userMetrics.currentRegistrationsPercent) },
                { value: Number(userMetrics.currentRegistrationsPercent) },
              ]}
              innerRingData={[
                { value: 100 - Number(userMetrics.previousRegistrationsPercent) },
                { value: Number(userMetrics.previousRegistrationsPercent) },
              ]}
              previousValue={userMetrics.previousRegistrations}
              currentValue={userMetrics.currentRegistrations}
              color="pink"
            />
            <UserMetricsPieChart
              title="Onboardings"
              outerRingData={[
                { value: 100 - Number(userMetrics.currentOnboardingsPercent) },
                { value: Number(userMetrics.currentOnboardingsPercent) },
              ]}
              innerRingData={[
                { value: 100 - Number(userMetrics.previousOnboardingsPercent) },
                { value: Number(userMetrics.previousOnboardingsPercent) },
              ]}
              previousValue={userMetrics.previousOnboardings}
              currentValue={userMetrics.currentOnboardings}
              color="purple"
            />
            <UserMetricsPieChart
              title="Verifications"
              outerRingData={[
                { value: 100 - Number(userMetrics.currentVerificationsPercent) },
                { value: Number(userMetrics.currentVerificationsPercent) },
              ]}
              innerRingData={[
                { value: 100 - Number(userMetrics.previousVerificationsPercent) },
                { value: Number(userMetrics.previousVerificationsPercent) },
              ]}
              previousValue={userMetrics.previousVerifications}
              currentValue={userMetrics.currentVerifications}
              color="blue"
            />
          </section>
        </section>
      </>
    );
  };

  const renderTable = () => {
    return (
      <section className="user-metrics__table">
        <Table
          data={userMetrics.userMetrics.map((userMetric) => ({
            username: userMetric.username,
            registered: formatDate(userMetric.registrationDate, true),
            onboarded: formatDate(userMetric.onboardingDate, true),
            verified: formatDate(userMetric.verificationDate, true),
            lastActive: formatDate(userMetric.lastActiveDate, true),
          }))}
          currentPage={0}
          totalPages={0}
          hiddenColumns={[]}
        />
      </section>
    );
  };

  return (
    <section className="user-metrics">
      <section className="user-metrics__header">
        <h1>User Metrics</h1>
        {showTable && (
          <DateRangeDropdown
            fromDate={fromDate}
            toDate={toDate}
            onFromDateChange={handleFromDateChange}
            onToDateChange={handleToDageChange}
          />
        )}
      </section>

      {userMetrics && (
        <section className={getClassNames('user-metrics__content', { table: showTable })}>
          {showTable ? renderTable() : renderGraphs()}
          <section className="user-metrics__buttons">
            <Button onClick={() => downloadExcel(fromDate, toDate)} secondary>
              Download .xlsx
            </Button>
            <Button onClick={() => setShowTable(!showTable)}>{showTable ? 'View as Graph' : 'View as Table'}</Button>
          </section>
        </section>
      )}
    </section>
  );
};

UserMetrics.propTypes = {
  userMetrics: PropTypes.shape({}),
  retrieveUserMetrics: PropTypes.func.isRequired,
  downloadExcel: PropTypes.func.isRequired,
};

UserMetrics.defaultProps = {
  userMetrics: undefined,
};

export default UserMetrics;
